import { ReactNode, useRef } from "react"

import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

export type TooltipProps = {
  content: ReactNode
  children: ReactNode
  dark?: boolean
} & Omit<
  TooltipPrimitive.TooltipContentProps & TooltipPrimitive.TooltipProps,
  "content"
>

export function Tooltip({
  children,
  content,
  dark = true,
  defaultOpen,
  open,
  onOpenChange,
  delayDuration = 200,
  disableHoverableContent = false,
  className,
  onPointerDownOutside,
  ...props
}: TooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)

  if (!content) return <>{children}</>

  return (
    <TooltipPrimitive.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      <TooltipPrimitive.Trigger ref={triggerRef} asChild>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal container={document.body}>
        <TooltipPrimitive.Content
          collisionPadding={8}
          sideOffset={8}
          className={cn(
            "max-w-[100vw] rounded-md px-4 py-2 text-body2 shadow",
            "animate-fadeIn origin-[var(--radix-tooltip-content-transform-origin)] print:hidden",
            {
              "bg-brand-primary-900 text-white": dark,
              "bg-white text-colors-text-primary": !dark,
            },
            className,
          )}
          style={{
            zIndex: 300,
          }}
          onPointerDownOutside={(event) => {
            onPointerDownOutside?.(event)
            if (triggerRef.current?.contains(event.target as Node)) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          {...props}
        >
          {content}
          <TooltipPrimitive.Arrow
            width={16}
            height={8}
            className={cn({
              "fill-brand-primary-900": dark,
              "fill-white": !dark,
            })}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

export const TooltipProvider = TooltipPrimitive.Provider
